/**
 * GENERATED FILE — DO NOT EDIT.
 *
 * Regenerate with `pnpm --filter @repo/api-client generate`, which reads the
 * OpenAPI document the API publishes at GET /v1/openapi.json.
 *
 * A hand-edit here fails `pnpm test`: the drift gate re-generates from the
 * checked-in openapi.json and compares byte for byte.
 */

import type { CredentialPlacement } from '../operation.ts';
import type { ErrorCode } from './models.ts';

/**
 * Whether the API's error registry permits a failure to be replayed.
 *
 * Contract, not advice: two failures never share a code, so the status alone
 * cannot decide this — a 409 is SYNC_REQUEST_IN_PROGRESS (retry shortly) or
 * SYNC_IDEMPOTENCY_KEY_REUSED (never). Every authentication and authorisation
 * code is false: those refusals are caused by the client and would fail
 * identically on replay.
 *
 * `satisfies Record<ErrorCode, boolean>` is the completeness check — a code
 * added to the document with no registry row fails to compile here.
 */
export const errorRetryability = {
  VALIDATION_FAILED: false,
  VALIDATION_UNKNOWN_FIELD: false,
  VALIDATION_PAYLOAD_TOO_LARGE: false,
  SYNC_IDEMPOTENCY_KEY_MISSING: false,
  SYNC_IDEMPOTENCY_KEY_REUSED: false,
  SYNC_REQUEST_IN_PROGRESS: true,
  SYNC_WRITE_CONTRACT_UNSUPPORTED: false,
  SYNC_OFFLINE_TICKET_ALREADY_RECORDED: false,
  SYNC_OFFLINE_TICKET_REASON_INVALID: false,
  BUSINESS_DAY_CLOSED: false,
  LEDGER_ENTRY_IMMUTABLE: false,
  LEDGER_AMOUNT_INVALID: false,
  RESOURCE_NOT_FOUND: false,
  AUTH_CREDENTIALS_INVALID: false,
  AUTH_ACCOUNT_LOCKED: false,
  AUTH_SESSION_EXPIRED: false,
  AUTH_FORBIDDEN: false,
  AUTH_CREDENTIAL_CHANGE_REQUIRED: false,
  AUTH_NO_OUTLET_ASSIGNMENT: false,
  AUTH_TERMINAL_LEASE_EXPIRED: false,
  AUTH_TERMINAL_REVOKED: false,
  IDENTITY_STAFF_CODE_TAKEN: false,
  IDENTITY_OUTLET_CODE_TAKEN: false,
  IDENTITY_OUTLET_TAX_IDENTITY_FIXED: false,
  IDENTITY_TERMINAL_PREFIX_TAKEN: false,
  IDENTITY_LAST_OWNER: false,
  IDENTITY_ROLE_ESCALATION: false,
  IDENTITY_CREDENTIAL_REQUIRED: false,
  CATALOGUE_CODE_TAKEN: false,
  CATALOGUE_BARCODE_TAKEN: false,
  CATALOGUE_ITEM_RETIRED: false,
  CATALOGUE_STOCK_NOT_AVAILABLE_HERE: false,
  CATALOGUE_STOCK_ON_NON_PRODUCT: false,
  CATALOGUE_PRICE_INVALID: false,
  CATALOGUE_VERSION_UNKNOWN: false,
  TICKETING_NUMBER_CONFLICT: false,
  TICKETING_TICKET_IMMUTABLE: false,
  TICKETING_ITEM_NOT_SELLABLE_HERE: false,
  TICKETING_ITEM_KIND_INVALID: false,
  TICKETING_PRICE_MISMATCH: false,
  TICKETING_PAYMENT_REFERENCE_NOT_ALLOWED: false,
  TICKETING_DAY_CLOSED: false,
  TICKETING_VOID_IMMUTABLE: false,
  TICKETING_TICKET_ENTRY_ALREADY_REVERSED: false,
  SALES_NUMBER_CONFLICT: false,
  SALES_SALE_IMMUTABLE: false,
  SALES_ITEM_NOT_SELLABLE_HERE: false,
  SALES_ITEM_KIND_INVALID: false,
  SALES_PRICE_MISMATCH: false,
  SALES_TOTAL_MISMATCH: false,
  SALES_INVOICE_NUMBER_MISMATCH: false,
  SALES_TAX_INVOICE_UNCONFIGURED: false,
  SALES_PAYMENT_REFERENCE_NOT_ALLOWED: false,
  SALES_EMPTY_BASKET: false,
  SALES_ALREADY_REFUNDED: false,
  SALES_DAY_CLOSED: false,
  SALES_REPRINT_NOT_ALLOWED: false,
  STOCK_ITEM_NOT_TRACKED: false,
  STOCK_OUTLET_CARRIES_NO_STOCK: false,
  STOCK_QUANTITY_INVALID: false,
  STOCK_REASON_INVALID: false,
  STOCK_DUPLICATE_ITEM_IN_REQUEST: false,
  STOCK_STOCKTAKE_IMMUTABLE: false,
  DAY_CLOSE_ALREADY_CLOSED: false,
  DAY_CLOSE_NOT_CLOSED: false,
  DAY_CLOSE_COUNT_INVALID: false,
  DAY_CLOSE_REASON_INVALID: false,
  DAY_CLOSE_PAYOUT_AMOUNT_INVALID: false,
  DAY_CLOSE_PAYOUT_REASON_INVALID: false,
  DAY_CLOSE_TERMINAL_DAY_CLOSED: true,
  REPORTING_PERIOD_INVALID: false,
  REPORTING_PERIOD_TOO_LONG: false,
  REPORTING_PERIOD_IN_FUTURE: false,
  REPORTING_UPI_ALREADY_DECLARED: false,
  REPORTING_UPI_DECLARATION_SUPERSEDED: false,
  REPORTING_REVENUE_SHARE_OVERLAP: false,
  REPORTING_REVENUE_SHARE_ALREADY_ENDED: false,
  REPORTING_OPENING_BALANCE_ALREADY_RECORDED: false,
  REPORTING_OPENING_BALANCE_SUPERSEDED: false,
  REPORTING_DAILY_SHEET_BEFORE_OPENING_BALANCE: false,
  REPORTING_PARK_EXPENSE_SUPERSEDED: false,
  EXPENSE_AMOUNT_INVALID: false,
  EXPENSE_REASON_INVALID: false,
  EXPENSE_VENDOR_NAME_TAKEN: false,
  EXPENSE_VENDOR_UNAVAILABLE: false,
  EXPENSE_ATTACHMENT_TYPE_UNSUPPORTED: false,
  EXPENSE_ATTACHMENT_LIMIT_REACHED: false,
  MACHINE_CODE_TAKEN: false,
  MACHINE_ATTACHMENT_BEHIND_IMPORTED_BILLS: false,
  MACHINE_PRODUCT_ITEM_RETIRED: false,
  MACHINE_IMPORT_FILE_UNREADABLE: false,
  MACHINE_IMPORT_HEADER_UNRECOGNISED: false,
  MACHINE_IMPORT_TOO_MANY_ROWS: false,
  MACHINE_IMPORT_ROWS_UNREADABLE: false,
  MACHINE_NOT_REGISTERED: false,
  MACHINE_NOT_ATTACHED_ON_DATE: false,
  MACHINE_PRODUCT_CODE_UNMAPPED: false,
  MACHINE_PRODUCT_CODE_REPROGRAMMED: false,
  MACHINE_BILL_VALUE_UNRECOGNISED: false,
  MACHINE_BILL_INCONSISTENT: false,
  MACHINE_BILL_AMOUNT_INVALID: false,
  MACHINE_BILL_DISCOUNT_UNSUPPORTED: false,
  MACHINE_BILL_DISCOUNT_EXCEEDS_AMOUNT: false,
  MACHINE_BILL_DATE_IN_FUTURE: false,
  MACHINE_BILL_DIFFERS: false,
  MACHINE_BILL_ALREADY_REVERSED: false,
  MACHINE_BILL_REVERSAL_REASON_INVALID: false,
  SYSTEM_REQUEST_REJECTED: false,
  SYSTEM_UNAVAILABLE: true,
  SYSTEM_INTERNAL_ERROR: true,
} as const satisfies Record<ErrorCode, boolean>;

/**
 * Where each credential goes on the wire. `session` is a person and travels
 * as a bearer token; `terminalLease` is a device and travels in its own
 * header. The document is explicit that the two must never be swapped.
 */
export const securitySchemes = {
  actingStaff: { kind: 'header', header: 'x-acting-staff-code' },
  session: { kind: 'bearer' },
  terminalLease: { kind: 'header', header: 'x-terminal-lease' },
} as const satisfies Record<string, CredentialPlacement>;
